import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useField } from 'remix-validated-form'
import ComboMultiSelectAsync from 'app/components/ui/combo/combo-multi-select-async'
import type { LocationJson, LocationType } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

type Props = {
  allLocations: boolean
  allowedLocations: LocationType[]
  isOrgLevel: boolean
  getFilterLocations: { [key: string]: string } | undefined
  onLocationChange: (locationIds: string[]) => void
  disabled?: boolean
}

export default function MultiLocationSelector({
  allLocations,
  allowedLocations,
  isOrgLevel,
  getFilterLocations,
  onLocationChange,
  disabled,
}: Props) {
  const { error } = useField(`allowedLocationIds`)
  const [selectedValues, setSelectedValues] = useState<string[]>([])

  const deselectAllOption: OptionType = {
    id: `0`,
    value: `*`,
    name: `All Locations`,
  }

  const optionsMapper = (response: SearchResponse<LocationJson>): OptionType[] => {
    debug(`location`, `mapper`, response)

    if (Array.isArray(response)) {
      return (response as unknown as string[]).map((id) => {
        if (id === `*`) return deselectAllOption
        const match = allowedLocations.find((loc) => loc.id === id)
        return { id, value: id, name: match?.name ?? id }
      })
    }

    const locationOptions =
      response.hits?.map((location) => ({
        id: location.id!,
        value: location.id!,
        name: location.name!,
      })) || []

    return locationOptions
  }

  const handleChangeHandler = (options?: OptionType[]) => {
    debug(`location`, `multiple change`, options)
    if (!options || options.length === 0) {
      setSelectedValues([])
      onLocationChange([])
      return
    }

    const allSelected = options.some((opt) => opt.id === `0` && opt.value === `*`)
    if (allSelected) {
      setSelectedValues([`*`])
      onLocationChange([])
    } else {
      const locationIds = options.filter((opt) => opt.id !== `0`).map((opt) => opt.value!)
      setSelectedValues(locationIds)
      onLocationChange(locationIds)
    }
  }

  useEffect(() => {
    debug(`location`, `sync with parent`, { allLocations, allowedLocations: allowedLocations.length })
    if (allLocations) {
      setSelectedValues([`*`])
    } else if (allowedLocations.length > 0) {
      const locationIds = allowedLocations.map((loc) => loc.id!)
      setSelectedValues(locationIds)
    } else {
      setSelectedValues([])
    }
  }, [allLocations, allowedLocations])

  const isAllSelected = selectedValues.length === 0 || selectedValues.includes(`*`)

  return (
    <div className="col-span-6">
      {isAllSelected && <input type="hidden" name="allLocations" value="on" />}

      <label
        htmlFor="allowedLocationIds"
        className={classNames(`text-ui-sm text-strong`, `required`, error && `text-red-200`)}
      >
        {`${isOrgLevel ? `Assigned ` : `Allowed`} Locations`}
      </label>
      <ComboMultiSelectAsync
        fetcher={createGqlFetcher('locations')}
        placeholder="Search for locations"
        optionsContainerClassName="max-h-[480px]"
        className="async-combo-selector"
        inputClassName="rounded-lg border border-default bg-surface px-4 py-3 text-body-sm shadow-elevation-1"
        additionalParams={getFilterLocations}
        values={selectedValues}
        queryValues={false}
        disabled={disabled}
        deselectAllOption={deselectAllOption}
        onChangeHandler={handleChangeHandler}
        optionsMapper={optionsMapper}
        enableDeselectAllOption
      />
      {error && <div className={classNames(`mt-[9px] text-body-sm text-red-200`)}>{error}</div>}
    </div>
  )
}
